import { useCallback, useState } from 'react'

import { type CustomField } from '@webapp/core/entities/internal/CustomField'
import { useDeletePOCustomFieldPresenter } from '@webapp/modules/purchasing/features/delete-po-custom-field/presenters/UseDeletePOCustomFieldPresenter'
import { usePOCustomFieldConfigListPresenter } from '@webapp/modules/purchasing/features/list-po-custom-field-configs/presenters/POCustomFieldConfigListPresenter'

/**
 * Orchestration hook for POCustomFieldsView.
 *
 * Owns the create-modal lifecycle (BHV-009/012) and bridges list data into
 * the create/rename guards. Never imports Model hooks — guard data comes
 * from the list feature's presenter companion hook, which React Query
 * dedupes with the rendered presenter's page-1 query (Feature Set Companion
 * Hook Pattern).
 */
export const usePOCustomFieldsView = () => {
  const [isCreateModalOpen, setIsCreateModalOpen] = useState(false)

  const openCreateModal = useCallback(() => {
    setIsCreateModalOpen(true)
  }, [])

  const closeCreateModal = useCallback(() => {
    setIsCreateModalOpen(false)
  }, [])

  // Headless companion use — guard data for the create max-count and
  // create/rename uniqueness checks
  const {
    data: { fields, totalCount },
  } = usePOCustomFieldConfigListPresenter()

  const {
    commands: { deleteField },
  } = useDeletePOCustomFieldPresenter()

  const onDeleteField = (field: CustomField) => {
    deleteField(field)
  }

  return {
    data: {
      fields,
      totalCount,
    },
    states: {
      isCreateModalOpen,
    },
    methods: {
      openCreateModal,
      closeCreateModal,
      onDeleteField,
    },
  }
}
